/**
 * Pictures in a conversation, on both sides of sending one (D-751).
 *
 * **One file for the tray and the grid**, because they draw the same thing at
 * two moments — what she is about to send, and what was sent. Splitting them
 * is how the two would end up with different corners and different alt text.
 */

import { useRef, useState } from 'react'
import { FileText, ImagePlus, Loader2, X } from 'lucide-react'

import Modal from './Modal'

import { aspectOf, attachmentUrl, describeSize, isPdf, isVideo, MAX_PICTURES_PER_MESSAGE, PICKER_ACCEPT } from '../api/attachments'
import type { MessageAttachment } from '../api/attachments'
import { describeWait, forecastUpload } from '../api/uploadEstimate'
import type { UploadForecast } from '../api/uploadEstimate'
import type { Measured } from '../api/mediaUpload'

/** One picture in the composer, from the moment she picks it. */
export type PendingPicture = {
  /** Stable for the life of the tray row; never the attachment id, which arrives late. */
  key: string
  name: string
  /** An object URL of the file she picked, so the thumbnail is instant and local. */
  previewUrl: string
  /**
   * What she picked, so the tray draws the right element (D-899).
   *
   * ⚠ **The tray drew an `<img>` for everything**, so a video she had just
   * chosen showed a broken-image icon with its filename under it — reported
   * from QA beside a genuine upload failure, which made one fault look like
   * two. Optional so an older caller still type-checks and gets the picture
   * behaviour it already had.
   */
  contentType?: string
  status: 'uploading' | 'shrinking' | 'ready' | 'failed'
  /**
   * How much of it has reached storage, 0 to 1 (D-901).
   *
   * **Absent means unknown, and that is a real state** — the upload through
   * the app reports nothing, and some mobile browsers cannot measure a large
   * body. Then the spinner alone is the honest answer; a fabricated percentage
   * would be worse than none.
   */
  progress?: number
  /**
   * Stops this tile's upload when she takes it back off (D-908).
   *
   * ⚠ **Removing a tile used to revoke its thumbnail and drop the row while
   * the transfer carried on.** On a photograph that was waste nobody saw; on a
   * video over cellular it is minutes of her data spent on something she has
   * already taken back, and it reads as a cancel button that does not cancel.
   */
  stop?: AbortController
  /**
   * What is known about making a smaller copy of this one (D-912).
   *
   * **Present only on a video she has not settled.** Grouped rather than four
   * more fields on the tile, because the three of them are only ever read
   * together — an offer needs the size, the speed *and* the length, and two out
   * of three is not an offer, it is a guess.
   */
  smaller?: {
    /** The original, so a second attempt has something to encode. */
    file: File
    /**
     * How long the video runs, off the preview element's own metadata.
     *
     * **Undefined is a refusal to offer, not a zero.** It counts the encode in
     * the quoted wait, and a browser that cannot read the metadata almost
     * certainly cannot encode the file either — so an offer made without it
     * risks being one we cannot honour, which is worse than no offer.
     */
    seconds?: number
    /**
     * What one real round of blocks said about her connection (D-912), speed
     * and bytes-already-gone together.
     *
     * **Both, because the speed alone cannot be acted on**: it is taken a round
     * in, and a smaller copy restarts from zero, so forecasting from the whole
     * file would offer a saving that on a small upload is a loss.
     */
    measured?: Measured
    /** She has chosen, or a copy has already been made. Either ends the offer. */
    settled?: boolean
    /**
     * What came of it, where that is worth a sentence.
     *
     * **A device that cannot encode has to say so.** She asked for something
     * and got the full video anyway; silence there is the
     * control-that-does-nothing this file already has a decision about (D-895).
     */
    said?: string
  }
  /**
   * What the current attempt is actually transferring, so a failed one can be
   * tried again (D-913).
   *
   * ⚠ **The compressed copy, not the original, where there was one.** A retry
   * that reached for `smaller.file` would put her through the whole encode a
   * second time for no reason — the bytes she chose to send already exist.
   *
   * **Absent means there is nothing to retry**, which is exactly right for a
   * tile refused by `refusalFor` before any transfer began: a file of the wrong
   * kind will be refused again, so offering to try it is offering a button that
   * cannot work.
   */
  sending?: { file: File; dimensions?: { width: number; height: number } }
  /**
   * Whether another go could plausibly work (D-913).
   *
   * **False for a file the browser cannot decode**, which fails identically for
   * ever — so the retry is withheld rather than offered as a button that cannot
   * work. Absent means the tile never got as far as transferring.
   */
  retryable?: boolean
  /** Present once the upload finished: this is what the message carries. */
  attachment?: MessageAttachment
  error?: string
}

/**
 * Whether to put the smaller-copy choice in front of her for this tile, and
 * the numbers to put in it (D-912).
 *
 * **One function, because two screens ask it** — the banner decides whether to
 * draw, and the composer decides what deadline to give the encoder, and those
 * two disagreeing is a banner quoting a saving the encoder is not aiming at.
 */
export function offerFor(picture: PendingPicture): UploadForecast | null {
  const smaller = picture.smaller

  if (!smaller || smaller.settled || picture.status !== 'uploading') return null
  if (smaller.seconds === undefined || smaller.measured === undefined) return null

  const forecast = forecastUpload(
    smaller.file.size,
    smaller.measured.bytesPerSecond,
    smaller.seconds,
    smaller.measured.confirmedBytes,
  )

  return forecast.worthOffering ? forecast : null
}

/**
 * What she has picked, under the box.
 *
 * **Shown from the instant she picks, not when the upload finishes.** A
 * photograph from a phone takes seconds on studio wifi, and a tray that
 * appeared at the end would read as nothing having happened.
 */
export function PictureTray({
  pictures,
  onRemove,
  onDuration,
  label = 'Pictures to send',
}: {
  pictures: PendingPicture[]
  onRemove: (key: string) => void
  /**
   * How long a picked video runs, the moment the browser knows (D-912).
   *
   * **This element is already loading the metadata**, for the thumbnail, so
   * reporting what it read costs nothing — where a second hidden `<video>`, or
   * opening the file with the demuxer, would both load a phone video's header
   * twice over on the connection she is already uploading on.
   */
  onDuration?: (key: string, seconds: number) => void
  label?: string
}) {
  if (pictures.length === 0) return null

  return (
    <ul aria-label={label} className="flex flex-wrap gap-2 px-1">
      {pictures.map((p) => (
        <li key={p.key} className="relative">
          {/*
            <b>The element the file actually is</b> (D-899). An object URL of a
            video in an `<img>` is a broken-image icon, which is what a teacher
            saw after picking one; a PDF is the same. The frame is identical in
            all three cases so the tray keeps its shape.
          */}
          {isVideo(p.contentType) ? (
            <video
              src={p.previewUrl}
              aria-label={p.name}
              muted
              playsInline
              // Enough to draw a first frame, without downloading a phone video
              // twice over on a connection she is already uploading on.
              preload="metadata"
              onLoadedMetadata={(e) => {
                const seconds = e.currentTarget.duration

                // A live stream reports Infinity and a header the browser could
                // not read reports NaN; neither is a length to forecast with.
                if (Number.isFinite(seconds) && seconds > 0) onDuration?.(p.key, seconds)
              }}
              className={`h-20 w-20 rounded-lg border border-line bg-ink object-cover ${
                p.status === 'ready' ? '' : 'opacity-60'
              }`}
            />
          ) : isPdf(p.contentType) ? (
            <span
              aria-label={p.name}
              className={`flex h-20 w-20 flex-col items-center justify-center gap-1 rounded-lg border border-line bg-surface-2 px-1 ${
                p.status === 'ready' ? '' : 'opacity-60'
              }`}
            >
              <FileText size={20} className="text-ink-2" aria-hidden="true" />
              <span className="w-full truncate text-center text-[10px] text-ink-3">{p.name}</span>
            </span>
          ) : (
            <img
              src={p.previewUrl}
              alt={p.name}
              className={`h-20 w-20 rounded-lg border border-line object-cover ${
                p.status === 'ready' ? '' : 'opacity-60'
              }`}
            />
          )}

          {/*
            **A percentage, because a spinner cannot say "working" (D-901).**
            A video from a phone is minutes, and what she reported was *"seemed
            to just hang"* against an upload that was very likely fine. The
            number is also the diagnosis: stuck at 40% is a dead connection,
            stuck at 100% is something after the transfer.
          */}
          {/*
            **The encode wears the same overlay as the transfer** (D-912), and
            says which it is. They are two waits with one appearance on purpose
            — a tile that went blank and came back would read as the upload
            having restarted, which is exactly what she must not think when she
            has just chosen to make it smaller.
          */}
          {(p.status === 'uploading' || p.status === 'shrinking') && (
            <span
              className="absolute inset-0 flex flex-col items-center justify-center gap-0.5 rounded-lg bg-surface/70"
              role="status"
              aria-label={
                (p.status === 'shrinking'
                  ? `Making ${p.name} smaller`
                  : `Sending ${p.name}`) +
                (p.progress === undefined ? '' : `, ${Math.round(p.progress * 100)}% done`)
              }
            >
              <Loader2 size={18} className="animate-spin text-ink-2" aria-hidden="true" />

              {p.progress !== undefined && (
                <span className="text-[10px] font-medium leading-none text-ink-2" aria-hidden="true">
                  {Math.round(p.progress * 100)}%
                </span>
              )}
            </span>
          )}

          {p.status === 'failed' && (
            <span className="absolute inset-x-0 bottom-0 rounded-b-lg bg-crit px-1 py-0.5 text-[10px] leading-tight text-on-crit">
              {p.error ?? 'Did not send'}
            </span>
          )}

          <button
            type="button"
            onClick={() => onRemove(p.key)}
            aria-label={`Remove ${p.name}`}
            className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full border border-line bg-surface text-ink-2 shadow-sm"
          >
            <X size={14} aria-hidden="true" />
          </button>
        </li>
      ))}
    </ul>
  )
}

/**
 * The choice, under the tray: this will take a while, or a smaller copy will
 * not (D-912).
 *
 * `asked`, the owner: *"what if we also looked at implementing something that
 * basically notifies the user when they are about to try and upload a large
 * file over cellular… flag that if they want the video to stay in higher
 * quality then they need to do it over wifi."*
 *
 * **Two named buttons rather than one.** Keeping the full video has a
 * consequence — seventeen minutes of her evening and her data — so it is a
 * thing she chooses, not the thing that happens when she ignores a prompt.
 * Ignoring it does keep the full video, which is why the quiet option has to
 * be the one with a label on it.
 *
 * **It appears mid-upload, and that is the point.** The wait it quotes is
 * measured off bytes storage has actually confirmed, not guessed from which
 * radio the phone is holding — `navigator.connection` cannot see sending at
 * all, and does not exist on an iPhone. The cost is that she reads this a few
 * seconds in, having already sent one block; the alternative is a prompt that
 * fires on wifi and teaches her to dismiss it.
 */
/**
 * A failed upload, and a way to try it again (D-913).
 *
 * `asked`, the owner, after a phone round where locking the screen killed an
 * upload: *"It said something about the upload failed and I was going to have
 * to retry but there wasn't anything to just click on to retry I had to just
 * start a new upload."*
 *
 * **The tile already said it failed; what it had was no handle.** Removing it
 * and picking the file again from the camera roll also pays for the encode a
 * second time, which is the part that stings on a phone.
 *
 * **Under the tray rather than on the tile**, for the same reasons the offer is:
 * an 80px tile has no room for a control that clears the 24px tap target the
 * layout sweep checks, and two failed tiles need their file names to tell their
 * buttons apart.
 *
 * **It covers every cause, not just a locked phone** — a dead connection that
 * exhausts the ladder lands here too, and until now that also had no handle.
 */
export function FailedUploads({
  pictures,
  onRetry,
}: {
  pictures: PendingPicture[]
  onRetry: (key: string) => void
}) {
  /*
   * **One discriminator, and it is the meaningful one.** `retryable` is written
   * only where an attempt actually failed, so it already excludes a tile
   * `refusalFor` turned away before any transfer began — and it is false for a
   * file the browser cannot decode. A second `sending !== undefined` check read
   * as defence and was in fact redundant, which makes it a guard no test could
   * ever fail for.
   */
  const stuck = pictures.filter((p) => p.status === 'failed' && p.retryable === true)

  if (stuck.length === 0) return null

  return (
    <div className="flex flex-col gap-2 px-1">
      {stuck.map((picture) => (
        <div
          key={picture.key}
          className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-crit bg-crit-bg px-3.5 py-3 text-[13px] text-ink"
        >
          {/*
            **The sentence is not repeated here.** The tile's own band already
            carries it, and saying it twice was caught by a pre-existing test
            finding two copies of one refusal on screen. This banner is the
            handle she did not have, not a second place to read the reason.
          */}
          <p className="min-w-0 flex-1 wrap-anywhere">
            <span className="font-medium">{picture.name}</span> did not send.
          </p>

          <button
            type="button"
            onClick={() => onRetry(picture.key)}
            aria-label={`Try sending ${picture.name} again`}
            className="min-h-11 shrink-0 rounded-lg bg-accent px-3.5 py-2 text-[13px] font-medium text-on-accent"
          >
            Try again
          </button>
        </div>
      ))}
    </div>
  )
}

export function SmallerCopyOffer({
  pictures,
  onShrink,
  onKeep,
}: {
  pictures: PendingPicture[]
  onShrink: (key: string) => void
  onKeep: (key: string) => void
}) {
  const offered = pictures.flatMap((picture) => {
    const forecast = offerFor(picture)
    return forecast ? [{ picture, forecast }] : []
  })

  const said = pictures.filter((picture) => picture.smaller?.said !== undefined)

  if (offered.length === 0 && said.length === 0) return null

  return (
    <div className="flex flex-col gap-2 px-1">
      {offered.map(({ picture, forecast }) => (
        <div
          key={picture.key}
          className="rounded-lg border border-warn bg-warn-bg px-3.5 py-3 text-[13px] text-ink"
        >
          <p className="font-medium">
            {picture.name} will take {describeWait(forecast.asIs)} to send on this connection.
          </p>

          {/*
            **The quality sentence is deliberately not a number.** "1080p" is
            true of the output and means nothing to a parent; what she is
            deciding is whether her teacher can still see the footwork, and
            "a little softer" is the honest answer to that in words she has.
          */}
          <p className="mt-0.5 text-ink-2">
            A smaller copy takes {describeWait(forecast.smaller)}. It stays high definition, a
            little softer.
          </p>

          <div className="mt-2.5 flex flex-wrap gap-2">
            {/*
              **The file is on the button, not only in the paragraph above it.**
              Two videos on one message produce two of these, and without the
              name a screen reader reads the same control twice — as would a
              test, which is the half that would have gone unnoticed.
            */}
            <button
              type="button"
              onClick={() => onShrink(picture.key)}
              aria-label={`Send a smaller copy of ${picture.name}`}
              className="min-h-11 rounded-lg bg-accent px-3.5 py-2 text-[13px] font-medium text-on-accent"
            >
              Send a smaller copy
            </button>

            <button
              type="button"
              onClick={() => onKeep(picture.key)}
              aria-label={`Keep the full video of ${picture.name}`}
              className="min-h-11 rounded-lg border border-line bg-surface px-3.5 py-2 text-[13px] font-medium text-ink-2"
            >
              Keep the full video
            </button>
          </div>
        </div>
      ))}

      {/*
        **What came of asking**, where it was not what she asked for. A phone
        that cannot reach an encoder sends the full video, and she has to be
        told that rather than left watching the original wait she was trying to
        avoid (D-895's rule, one medium up).
      */}
      {said.map((picture) => (
        <p key={picture.key} className="px-1 text-[13px] text-ink-2" role="status">
          {picture.smaller!.said}
        </p>
      ))}
    </div>
  )
}

/**
 * The button that opens the file picker, and the input behind it.
 *
 * **The input is hidden rather than styled**, which is the only way to get a
 * control that looks like the rest of the composer and still opens the
 * platform picker. It is labelled, so the button is what everything — a
 * screen reader included — sees.
 */
export function AddPictures({
  onFiles,
  disabled,
  full,
  label = 'Add a photo',
}: {
  onFiles: (files: File[]) => void
  disabled?: boolean
  full?: boolean
  label?: string
}) {
  const input = useRef<HTMLInputElement>(null)

  return (
    <>
      <input
        ref={input}
        type="file"
        accept={PICKER_ACCEPT}
        multiple
        hidden
        onChange={(e) => {
          onFiles([...(e.target.files ?? [])])
          // Cleared, or picking the same photograph twice in a row does nothing
          // the second time - the input's value has not changed.
          e.target.value = ''
        }}
      />

      <button
        type="button"
        onClick={() => input.current?.click()}
        disabled={disabled || full}
        title={full ? `Up to ${MAX_PICTURES_PER_MESSAGE} pictures in one message` : undefined}
        aria-label={label}
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-line text-ink-2 disabled:opacity-50"
      >
        <ImagePlus size={18} aria-hidden="true" />
      </button>
    </>
  )
}

/**
 * The pictures on a message that was sent.
 *
 * **Every tile holds its shape before the bytes arrive** (`aspect-ratio` from
 * the uploader's own numbers). Without it each photograph shoves the
 * conversation down as it decodes — which, on a live stream, happens while
 * somebody is reading (D-750).
 */
export function PictureGrid({
  conversationId,
  pictures,
  urlFor = attachmentUrl,
  mayOpen,
}: {
  conversationId: string
  pictures: MessageAttachment[]
  /** The review screen reads the same pictures through the studio's own door (D-626). */
  urlFor?: (conversationId: string, attachmentId: string) => string
  /**
   * Whether this tap should open the picture at all.
   *
   * <b>A long press on a bubble reveals its actions</b> (D-715), and on a
   * message that is only a photograph the picture <i>is</i> the bubble - so
   * without this, holding it would show the toolbar and open the picture on
   * top of it. The row owns that state; this asks it rather than measuring the
   * press a second way. The click is deliberately <b>not</b> stopped from
   * bubbling: the row's own handler is what clears the flag afterwards.
   */
  mayOpen?: () => boolean
}) {
  const [open, setOpen] = useState<MessageAttachment | null>(null)

  if (pictures.length === 0) return null

  return (
    <>
      {/*
        <b>A width, not a maximum</b> (D-255's shape). `max-w-[280px]` alone
        makes the grid size to its content, and a bubble hugs its content too -
        so a picture the browser has not measured yet collapses the whole thing.
        Observed by the deep sweep at 390px: a <b>3x3</b> tap target on the
        button wrapping the photograph. It hides behind any wide picture, which
        is exactly why it needed a browser to find.
      */}
      <ul
        className={`mt-1 grid w-[280px] max-w-full gap-1 ${pictures.length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}
      >
        {pictures.map((p, i) => (
          <li key={p.id} className="min-w-0">
            {/*
              **A PDF is a file, not a picture** (2026-10-07). Putting one
              through the `img` below renders a broken image and a lightbox
              that opens nothing — so it gets a row that says what it is and
              opens in a tab, where a browser renders it without anybody
              downloading and running anything.
            */}
            {/*
              **A video plays where it sits.** Opening it in the lightbox would
              be a picture viewer holding a video, and sending it to a tab makes
              a teacher leave the conversation to watch three seconds of
              choreography. `preload="metadata"` fetches the first frame and
              nothing else, so a thread with several does not pull hundreds of
              megabytes nobody asked for.
            */}
            {isVideo(p.contentType) ? (
              <video
                src={urlFor(conversationId, p.id)}
                controls
                preload="metadata"
                playsInline
                className="w-full max-w-full rounded-lg border border-line bg-surface-2"
              />
            ) : isPdf(p.contentType) ? (
              <a
                href={urlFor(conversationId, p.id)}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => {
                  if (mayOpen && !mayOpen()) e.preventDefault()
                }}
                className="flex min-h-[44px] w-full items-center gap-2 rounded-lg border border-line px-3 py-2 text-[13px] text-ink no-underline hover:border-line-2"
              >
                <FileText size={16} aria-hidden="true" className="shrink-0 text-ink-3" />
                <span className="min-w-0 truncate">{p.fileName || 'Document'}</span>
              </a>
            ) : (
            <button
              type="button"
              onClick={() => {
                if (mayOpen && !mayOpen()) return

                setOpen(p)
              }}
              /*
               * The button carries the name, so the picture inside it does
               * not: a screen reader reads the label and skips an alt it has
               * already been given. A file name is a poor description of a
               * photograph either way - what a reader needs here is which one
               * of several this is.
               */
              aria-label={
                pictures.length === 1 ? 'Open the picture' : `Open picture ${i + 1} of ${pictures.length}`
              }
              className="block w-full overflow-hidden rounded-lg border border-line"
            >
              <img
                src={urlFor(conversationId, p.id)}
                alt=""
                loading="lazy"
                style={{ aspectRatio: pictures.length === 1 ? aspectOf(p) : '1 / 1' }}
                className="h-full w-full max-w-full object-cover"
              />
            </button>
            )}
          </li>
        ))}
      </ul>

      {open && (
        <Lightbox
          picture={open}
          src={urlFor(conversationId, open.id)}
          onClose={() => setOpen(null)}
        />
      )}
    </>
  )
}

/**
 * One picture, as large as the window allows.
 *
 * **On the app's one overlay contract** (D-515) rather than a hand-built
 * `role="dialog"`. The first cut of this was the second kind: it trapped no
 * focus while telling assistive tech the page behind it was inert, listened for
 * Escape on `window` outside the shell's stack, and — worst on a phone —
 * dismissed when the photograph itself was tapped, which is the first thing
 * anybody does. All three are exactly what D-515 swept up, re-invented one
 * screen over.
 *
 * **No download link.** The bytes are behind a seat check the studio can
 * withdraw (D-751), and an anchor with `download` would put a copy on the
 * reader's disk with one tap. Long-pressing the picture is still the
 * platform's to offer; what this screen must not do is invite it.
 */
function Lightbox({
  picture,
  src,
  onClose,
}: {
  picture: MessageAttachment
  src: string
  onClose: () => void
}) {
  return (
    <Modal
      onDismiss={onClose}
      label={picture.fileName}
      overlayClassName="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
      panelClassName="flex max-h-full flex-col items-center gap-3"
    >
      <img
        src={src}
        alt=""
        className="max-h-[70vh] max-w-full rounded-lg object-contain"
      />

      <p className="text-[12px] text-white/80">
        {picture.fileName} &middot; {describeSize(picture.sizeBytes)}
      </p>

      <button
        type="button"
        onClick={onClose}
        className="min-h-[44px] rounded-lg border border-white/40 px-4 text-[13.5px] font-semibold text-white"
      >
        Close
      </button>
    </Modal>
  )
}
